<template>
  <div
    v-click-outside="{
      handler: close,
      closeConditional,
      include: include,
    }"
  >
    <v-navigation-drawer
      :key="`table-revisit-drawer-${hallId}-${tableNumber}`"
      ref="drawer"
      :value="isVisible"
      app
      right
      temporary
      stateless
      width="400"
      class="table-revisit-drawer"
    >
      <!-- NOTE v-app-barのv-ifは設定しないとHall.vueのタブ移動でなぜか一瞬表示されてしまう問題対策 -->
      <v-app-bar
        v-if="isVisible"
        outlined
        fixed
        flat
        style="z-index: 10"
      >
        <v-app-bar-nav-icon>
          <v-btn
            fab
            small
            :ripple="false"
            @click="close"
          >
            <v-icon>
              {{ icons.mdiDotsGrid }}
            </v-icon>
          </v-btn>
        </v-app-bar-nav-icon>

        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="d-flex">
              <div @click.stop>
                <v-select
                  v-model="selectedTableNumber"
                  :items="movableTables"
                  :prepend-icon="icons.mdiArrowRightBold"
                  dense
                  required
                  :rules="[isValidTableNumber]"
                  :error="!selectedTableNumber"
                  :error-messages="!selectedTableNumber ? [`利用可能な${tableSuffix}を選択してください`] : []"
                  @change="changeHallIdAndTableNumber"
                />
              </div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-app-bar>

      <v-card
        elevation="0"
        tile
      >
        <v-card-text class="pb-16">
          <v-card
            elevation="0"
            class="mb-7 mt-10"
          >
            <v-card-subtitle
              class="text-sm pa-0 pt-3 mb-5"
            >
              客数
            </v-card-subtitle>
            <div
              class="d-flex align-center flex-wrap px-1 customer-counter-input-wrapper"
            >
              <number-counter
                v-if="isVisible"
                :number="customerCount"
                :min="1"
                :open-by-default="isVisible"
                class="customer-counter-input"
                @change="updateCustomerCount"
              >
                <template #prepend-number>
                  <v-icon>
                    {{ icons.mdiAccountOutline }}
                  </v-icon>
                </template>
              </number-counter>
            </div>

            <customer-table-tagging
              :customer-table-taggings="customerTableTaggings"
              @updated="setCustomerTableTaggings"
            />
          </v-card>

          <v-divider class="mt-2 mb-1 mx-4" />

          <revisit-referring-histories
            :table-id="tableId"
            :referring-histories-temp="referringHistoriesTemp"
            @add:referring-history-temp="updateReferringHistoriesTemp"
            @remove:referring-history-temp="deleteReferringHistoryTemp"
          />

          <v-divider class="mt-2 mb-1 mx-4" />
          <!-- 指名(ノミネーション) start -->
          <v-card
            v-if="nominations.length > 0 && isNominationOrder"
            elevation="0"
            class="my-10 nomination-order-cards"
          >
            <v-card-subtitle
              class="text-sm pa-0 d-flex justify-space-between align-center pb-2 nomination-subtitle"
            >
              {{ currentClub.nominationAlias }}
              <v-btn
                icon
                :ripple="false"
                color="primary"
                class="nomination-add-btn"
                @click="setNominationOrder({
                  vendibleId: nominations[0].id,
                  vendibleType: 'Nomination',
                  sellingPrice: nominations[0].attributes.sellingPrice,
                })"
              >
                <v-icon>
                  {{ icons.mdiPlus }}
                </v-icon>
              </v-btn>
            </v-card-subtitle>

            <!-- eslint-disable vue/v-on-event-hyphenation -->
            <transition-group
              name="fadeUp"
              class="d-flex"
              :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : 'flex-column-reverse'"
            >
              <div
                v-for="(nominationHistoryTemp, nominationHistoryTempIdx) in nominationHistoriesTemp"
                :key="`nominationHistoriesTemp[${nominationHistoryTempIdx}]`"
                style="animation-duration: 0.3s"
              >
                <order-card
                  :id="`nomination-card-${nominationHistoryTempIdx}`"
                  :key="`nomination-order-card-${nominationHistoryTempIdx}/${nominationHistoriesTemp.length}`"
                  v-model="nominationHistoryTemp.value"
                  :vendibles="nominations"
                  :users="users"
                  :size="nominationHistoryTemp.size"
                  :table-customer-ids="customerIds"
                  class="mt-3 mb-8"
                  @update:vendibleId="nominationHistoryTemp.value.vendibleId = $event"
                  @update:vendibleType="nominationHistoryTemp.value.vendibleType = $event"
                  @update:sellingPrice="nominationHistoryTemp.value.sellingPrice = $event"
                  @update:taxCharge="nominationHistoryTemp.value.taxCharge = $event"
                  @update:vendingContributionsAttributes="nominationHistoryTemp.value.vendingContributionsAttributes = $event"
                  @update:isArbitraryVendingContribution="nominationHistoryTemp.isArbitraryVendingContribution = $event"
                  @update:vendibleRecipientsAttributes="nominationHistoryTemp.value.vendibleRecipientsAttributes = $event"
                  @update:vendingHistoryCustomersAttributes="nominationHistoryTemp.value.vendingHistoryCustomersAttributes = $event"
                  @update:size="nominationHistoryTemp.size = $event"
                  @remove="removeVendingHistoryTempByValue(nominationHistoryTemp.value)"
                />
              </div>
            </transition-group>
          </v-card>
          <!-- ノミネーション end -->

          <!-- 延長コース start -->
          <extension-candidate-courses
            :key="ExtensionCandidateCoursesCompKey"
            :table-id="tableId"
            :table-number="tableNumber"
            :table-prefix="tablePrefix"
            :table-suffix="tableSuffix"
            :customer-count="customerCount"
            :customer-ids="customerIds"
            :extension-candidates="extensionCandidates"
            :is-initializing="isInitializing"
            :can-create-table="canCreateTable"
            @updated:vending-histories="$emit('updated:vending-histories', $event)"
            @updated:table="$emit('updated:table', $event)"
            @reload="ExtensionCandidateCoursesCompKey = `${Math.random()}`"
            @order="createTable($event.vendingHistories, $event.customerIds)"
            @remove:nomination-history-temp="removeNominationHistoryTemp"
            @remove:item-history-temp="removeItemHistoryTemp"
            @remove:new-history-temp="removeNewHistoryTemp"
          />
          <!-- 延長コース end -->
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  inject,
  provide,
  getCurrentInstance,
  onMounted,
} from '@vue/composition-api'
import {
  mdiDotsGrid,
  mdiPlus,
  mdiViewGridPlus,
  mdiCashMarker,
  mdiAccountOutline,
  mdiAccountCircleOutline,
} from '@mdi/js'
import { cloneDeep, each, findIndex } from 'lodash'
import { useQrCodeSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import { format, parseISO } from 'date-fns'
import useQueryString from '@/views/composable/useQueryString'
import useVendingHistory from '@/views/composable/useVendingHistory'
import useReferral from '@/views/composable/useReferral'
import usePreventBackgroundScrolling from '@/views/composable/usePreventBackgroundScrolling'
import TableApi from '@/api/waiter/Table'
import ExtensionProposalApi from '@/api/v2/order/ExtensionProposal'
import useCurrentData from '@/views/composable/useCurrentData'
import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import NumberCounter from '@/views/components/util/NumberCounter.vue'
import OrderCard from '@/views/components/table/OrderCard/OrderCard.vue'
import CustomerTableTagging from '../CustomerTableTagging.vue'
import RevisitReferringHistories from './RevisitReferringHistories.vue'
import ExtensionCandidateCourses from './ExtensionCandidateCourses.vue'

export default {
  components: {
    CustomerTableTagging,
    NumberCounter,
    OrderCard,
    RevisitReferringHistories,
    ExtensionCandidateCourses,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    hallId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    receiptId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      default: null,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    halls: {
      type: Array,
      default: () => [],
    },
    customerTableTaggings: {
      type: Array,
      default: () => [],
    },
    customerIds: {
      type: Array,
      default: () => [],
    },
    referringHistories: {
      type: Array,
      default: () => [],
    },
    nominationHistories: {
      type: Array,
      default: () => [],
    },
    startAt: {
      type: String,
      default: '',
    },
    expireAt: {
      type: String,
      default: '',
    },
    customerCount: {
      type: Number,
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    referrals: {
      type: Array,
      default: () => [],
    },
    nominations: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { router } = useQueryString()
    const { currentClub } = useCurrentData()
    const { printer: receiptPrinter, checkConnection: checkReceiptPrinterConnection } = useReceiptPrinterConnection()
    const isCreating = ref(false)
    const ExtensionCandidateCoursesCompKey = ref(`${Math.random()}`)
    const selectedTableNumber = ref(`${props.hallId}-${props.tableNumber}`)
    const isNominationOrder = ref(false)

    const users = inject('users', [])
    const extensionCandidates = ref([])
    const isInitializing = ref(true)
    const extensionProposals = ref([])

    const {
      referringHistoriesTemp,
      addReferringHistoryTemp,
      resetReferringHistoriesTemp,
      isValidToSubmit: isValidReferringHistoriesToSubmit,
    } = useReferral()

    const {
      vendingHistoriesTemp,
      nominationHistoriesTemp,
      courseHistoriesTemp,
      addVendingHistoryTemp,
      removeVendingHistoryTempByValue,
      resetVendingHistoriesTemp,
      isValidToSubmit: isValidVendingHistoriesToSubmit,
      buildVendingHistoryTemp,
      setVendingContributionsAttributes,
    } = useVendingHistory()

    const { target: drawer } = usePreventBackgroundScrolling()

    const isValidContents = computed(() => {
      return isValidReferringHistoriesToSubmit.value && isValidVendingHistoriesToSubmit.value && extensionCandidates.value.length > 0
    })

    const sortedHalls = computed(() => {
      return props.halls.slice().sort((a, b) => {
        return a.attributes.position - b.attributes.position
      })
    })

    const movableTables = computed(() => {
      return sortedHalls.value.flatMap(hall => {
        // 現在進行中のテーブル番号を取得
        const ongoingTableNumbers = hall.attributes.tables.data.map(table => table.attributes.tableNumber)

        // テーブルリスト生成
        const tables = Array.from({ length: hall.attributes.tableCount }, (_, i) => {
          const tableNumber = i + 1

          // 現在進行中のテーブルを除外
          if (ongoingTableNumbers.includes(tableNumber)) return null

          return {
            text: [hall.attributes.tablePrefix, tableNumber, hall.attributes.tableSuffix].join(''),
            value: `${hall.id}-${tableNumber}`,
          }
        }).filter(Boolean) // null を除外

        return [{ header: hall.attributes.name }, ...tables]
      })
    })

    const isValidTableNumber = computed(() => {
      return selectedTableNumber.value.length > 0 && +selectedTableNumber.value.split('-')[0] === +props.hallId
    })

    const canCreateTable = computed(() => {
      return movableTables.value.length > 0 && isValidTableNumber.value && isValidContents.value
    })

    const setCustomerTableTaggings = tags => {
      emit('updated:customer-table-taggings', tags.map(tagging => {
        return {
          attributes: {
            tableId: props.tableId,
            customerTagId: tagging.customerTagId,
            customerTag: {
              data: {
                attributes: {
                  id: tagging.id,
                  type: 'customerTag',
                  ...tagging.attributes,
                },
              },
            },
          },
        }
      }))
    }

    const removeNominationHistoryTemp = (extensionCandidateIdx, nominationHistoryTempValue) => {
      const targetNominationHistoriesTemp = extensionCandidates.value[extensionCandidateIdx].nominationHistories
      const index = findIndex(targetNominationHistoriesTemp, { value: nominationHistoryTempValue })

      targetNominationHistoriesTemp.splice(index, 1)
    }

    const removeItemHistoryTemp = (extensionCandidateIdx, itemHistoryTempValue) => {
      const targetItemHistoriesTemp = extensionCandidates.value[extensionCandidateIdx].itemHistories
      const index = findIndex(targetItemHistoriesTemp, { value: itemHistoryTempValue })

      targetItemHistoriesTemp.splice(index, 1)
    }

    const removeNewHistoryTemp = (extensionCandidateIdx, newHistoryTempValue) => {
      const targetNewHistoriesTemp = extensionCandidates.value[extensionCandidateIdx].newHistories
      const index = findIndex(targetNewHistoriesTemp, { value: newHistoryTempValue })

      targetNewHistoriesTemp.splice(index, 1)
    }

    const resetExtensionCandidateCourses = () => {
      // 各extensionCandidateの配列プロパティを新しい参照で置き換える
      extensionCandidates.value = extensionCandidates.value.map(extensionCandidate => ({
        ...extensionCandidate,
        courseHistory: {
          ...extensionCandidate.courseHistory,
          value: {
            ...extensionCandidate.courseHistory.value,
            vendibleId: null,
            sellingPrice: null,
          },
        },
        nominationHistories: [],
        itemHistories: [],
        newHistories: [],
      }))
      // NOTE: 選択が解除されるためため新規で指名をセットできるようにする
      isNominationOrder.value = true
    }

    const initVendingHistoriesTemp = async incomingExtensionProposals => {
      extensionCandidates.value = incomingExtensionProposals.map(extensionProposal => {
        const courseHistory = buildVendingHistoryTemp({
          vendibleId: extensionProposal.course.vendibleId,
          vendibleType: extensionProposal.course.vendibleType,
          sellingPrice: extensionProposal.course.sellingPrice,
          size: extensionProposal.course.size,
          taxCharge: extensionProposal.course.taxCharge,
        })

        const nominationHistories = extensionProposal.nominations.map(nomination => {
          return buildVendingHistoryTemp({
            vendibleId: nomination.vendibleId,
            vendibleType: nomination.vendibleType,
            sellingPrice: nomination.sellingPrice,
            size: nomination.size,
            taxCharge: nomination.taxCharge,
            vendibleRecipientsAttributes: nomination.vendibleRecipientsAttributes,
          })
        })

        const itemHistories = extensionProposal.items.map(item => {
          return buildVendingHistoryTemp({
            vendibleId: item.vendibleId,
            vendibleType: item.vendibleType,
            sellingPrice: item.sellingPrice,
            size: item.size,
            taxCharge: item.taxCharge,
            vendibleRecipientsAttributes: item.vendibleRecipientsAttributes,
          })
        })

        const newHistories = []

        return {
          courseHistory,
          nominationHistories,
          itemHistories,
          newHistories,
        }
      })
    }

    const setNominationOrder = ({ vendibleId, vendibleType, sellingPrice }) => {
      isNominationOrder.value = true
      addVendingHistoryTemp({
        vendibleId,
        vendibleType,
        sellingPrice,
      })
    }

    const setVendingHistoriesTemp = async () => {
      isInitializing.value = true
      const res = await ExtensionProposalApi.getExtensionProposals({ tableId: props.tableId })
      if (res?.data) {
        extensionProposals.value = res.data
        initVendingHistoriesTemp(extensionProposals.value)
      }
      isInitializing.value = false
    }

    const isSameHallId = concatids => {
      return +concatids.split('-')[0] === props.hallId
    }

    const changeHallIdAndTableNumber = event => {
      if (!isSameHallId(event)) {
        resetExtensionCandidateCourses()
      }
      emit('updated:hall-id-table-number', event)
    }
    // NOTE: ドロワーが開かれた時に
    // セット付きの卓であればcourseHistoryTempをつくる
    watch(
      () => props.isVisible,
      (newVal, _) => {
        if (!newVal) return
        if (courseHistoriesTemp.value.length < 1 && props.courses[0]?.id) {
          addVendingHistoryTemp({
            vendibleId: props.courses[0].id,
            vendibleType: 'Course',
            sellingPrice: props.courses[0].attributes.sellingPrice,
          })
        }
        // 前回の注文で指名があったら延長で指名をセットするので表示しない
        if (!props.nominationHistories[0]?.id) {
          isNominationOrder.value = true
        }
        // selectedTableNumberがmovableTablesの中になかったら空にする
        if (selectedTableNumber.value) {
          const flatTables = movableTables.value.filter(table => !table.header)
          const exists = flatTables.some(table => table.value === selectedTableNumber.value)
          if (!exists) {
            selectedTableNumber.value = ''
          }
        }
      },
      { immediate: true },
    )

    const setReferringHistoriesData = () => {
      each(props.referringHistories, referringHistory => {
        addReferringHistoryTemp({
          id: referringHistory.id,
          userId: referringHistory.attributes.user.data.id,
          referralId: referringHistory.attributes.referral.data.id,
        })
      })
    }

    const updateReferringHistoriesTemp = newReferringHistoriesTemp => {
      if (newReferringHistoriesTemp.id) {
        addReferringHistoryTemp({
          id: newReferringHistoriesTemp.id,
          userId: newReferringHistoriesTemp.attributes.user.data.id,
          referralId: newReferringHistoriesTemp.attributes.referral.data.id,
        })
      } else {
        addReferringHistoryTemp(newReferringHistoriesTemp)
      }
    }

    const deleteReferringHistoryTemp = referringHistoryTempIdx => {
      referringHistoriesTemp.value.splice(referringHistoryTempIdx, 1)
    }

    const reset = () => {
      resetReferringHistoriesTemp()
      resetVendingHistoriesTemp()
      selectedTableNumber.value = null
    }

    const close = () => {
      reset()
      emit('close')
    }

    const setNewVendingHistories = vendingHistories => {
      return vendingHistories.map(vendingHistory => {
        return Array(vendingHistory.size).fill(vendingHistory.value)
      }).flat()
    }

    // QRコード印刷: 卓作成時の自動印刷用
    const qrPrintOptions = ref({
      ipAddress: '',
      qrCodeUrl: '',
      hallName: '',
      tableName: '',
      startAt: '',
    })
    const qrPrintOptionsComputed = computed(() => ({
      ...qrPrintOptions.value,
      printer: receiptPrinter.value,
    }))
    const { send: sendQrSlipPrint, onReceive: onReceiveQrPrint } = useQrCodeSlipPrint(qrPrintOptionsComputed)
    onReceiveQrPrint.value = response => {
      if (!response.success) {
        vm.$toast.error('QRコードの印刷に失敗しました')
      }
    }

    const printQrCodeForTable = async tableData => {
      try {
        const res = await TableApi.getSelfOrderQr(tableData.id)
        const data = res.data.data || res.data

        // セキュアモード（動的QR）の場合のみ自動印刷
        if (data.security_level !== 'secure') return

        const tableName = `${props.tablePrefix}${tableData.attributes.tableNumber}${props.tableSuffix}`

        let formattedStartAt = ''
        if (tableData.attributes.startAt) {
          try {
            formattedStartAt = format(parseISO(tableData.attributes.startAt), 'yyyy/MM/dd HH:mm')
          } catch {
            formattedStartAt = tableData.attributes.startAt
          }
        }

        qrPrintOptions.value = {
          ipAddress: removePortFromIpAddress(currentClub.value?.printIpAddress || ''),
          qrCodeUrl: data.url,
          hallName: tableData.attributes.hallName || '',
          tableName,
          startAt: formattedStartAt,
        }

        await checkReceiptPrinterConnection()
        await sendQrSlipPrint()
        vm.$toast.success('QRコードを印刷しました')
      } catch (error) {
        console.error('QRコード自動印刷エラー:', error)
        vm.$toast.error('QRコードの印刷に失敗しました')
      }
    }

    const createTable = async (extensionVendingHistories, customerIds = []) => {
      isCreating.value = true

      if (extensionVendingHistories.length > 0 && isValidContents.value) {
        try {
          const res = await TableApi.revisitTable({
            receiptId: props.receiptId,
            hallId: props.hallId,
            tableNumber: props.tableNumber,
            customerCount: props.customerCount,
            customerIds: props.customerIds,
            referringHistories: referringHistoriesTemp.value.map(referringHistory => {
              return {
                userId: referringHistory.userId,
                referralId: referringHistory.referralId,
              }
            }),
            vendingHistories: [
              ...setNewVendingHistories(extensionVendingHistories),
              ...setNewVendingHistories(nominationHistoriesTemp.value),
            ],
            customerTableTaggings: props.customerTableTaggings.map(tagging => {
              return {
                customerTagId: tagging.attributes.customerTagId,
              }
            }),
          })

          if (res?.data?.data) {
            emit('created', res.data.data)
            vm.$toast.success(`${[props.tablePrefix, props.tableNumber, props.tableSuffix].join('')}を作成しました`)

            // セルフオーダーがopenの場合、QRコードを自動印刷
            if (res.data.data.attributes.selfOrderStatus === 'open') {
              printQrCodeForTable(res.data.data)
            }
          }

          close()
        } catch (error) {
          if (error?.response?.status === 422 && error?.response?.data?.message) {
            emit('reload-table')
            vm.$toast.error(error.response.data.message)
            selectedTableNumber.value = ''
          } else {
            vm.$toast.error('テーブル作成に失敗しました')
          }
          isCreating.value = false

          return
        }
      }
      isCreating.value = false
      // hallsに移動する
      router.push({
        name: 'waiter-halls',
      })
    }

    onMounted(() => {
      setReferringHistoriesData()
      setVendingHistoriesTemp()
    })

    // NOTE: 販売貢献人をvendingHistoryTempに自動セットするwatch
    watch(
      [
        () => cloneDeep(vendingHistoriesTemp.value),
        () => cloneDeep(referringHistoriesTemp.value),
      ],
      (
        [_new1, _new2],
        [_prev1, _prev2],
      ) => {
        setVendingContributionsAttributes({
          targetVendingHistoriesTemp: vendingHistoriesTemp.value,
          targetReferringHistoriesTemp: referringHistoriesTemp.value,
        })
      },
    )

    const updateCustomerCount = newCount => {
      emit('updated:customer-count', newCount)
    }

    return {
      // data
      users,
      isCreating,
      referringHistoriesTemp,
      drawer,
      ExtensionCandidateCoursesCompKey,
      selectedTableNumber,
      isNominationOrder,
      extensionCandidates,
      isInitializing,
      extensionProposals,

      // computed
      nominationHistoriesTemp,
      courseHistoriesTemp,
      isValidContents,
      isValidReferringHistoriesToSubmit,
      isValidVendingHistoriesToSubmit,
      movableTables,
      canCreateTable,
      isValidTableNumber,
      // methods
      addReferringHistoryTemp,
      deleteReferringHistoryTemp,
      updateReferringHistoriesTemp,
      addVendingHistoryTemp,
      setNominationOrder,
      removeVendingHistoryTempByValue,
      createTable,
      close,
      updateCustomerCount,
      changeHallIdAndTableNumber,
      closeConditional: () => !!document.querySelector('.v-overlay--active') && props.isVisible,
      include: () => [
        ...document.querySelectorAll('.v-navigation-drawer--open'),
        ...document.querySelectorAll('.v-menu__content'),
        ...document.querySelectorAll('.v-dialog__content'),
        ...document.querySelectorAll('.Vue-Toastification__toast'),
      ],
      setCustomerTableTaggings,
      removeNominationHistoryTemp,
      removeItemHistoryTemp,
      removeNewHistoryTemp,
      icons: {
        mdiDotsGrid,
        mdiPlus,
        mdiViewGridPlus,
        mdiCashMarker,
        mdiAccountOutline,
        mdiAccountCircleOutline,
      },
    }
  },
}
</script>
